(() => {
  const rootSelector = '[data-cross-referral]';
  const itemSelector = '[data-cross-referral-item]';
  const dropdownSelector = '[data-cross-referral-dropdown]';

  const cents = (value) => Number.parseInt(value || '0', 10) || 0;

  function formatAmount(centsValue, token) {
    const value = centsValue / 100;

    switch (token) {
      case 'amount_no_decimals':
        return Math.round(value).toString();
      case 'amount_with_comma_separator':
        return value.toFixed(2).replace('.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return Math.round(value).toString();
      case 'amount_no_decimals_with_space_separator':
        return Math.round(value).toString();
      case 'amount_with_apostrophe_separator':
        return value.toFixed(2).replace('.', "'");
      default:
        return value.toFixed(2);
    }
  }

  function formatMoney(root, centsValue) {
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const formatted = moneyFormat.replace(/\{\{\s*(amount(?:_[^}]*)?)\s*\}\}/g, (_match, token) =>
      formatAmount(centsValue, token)
    );

    return formatted.replace(/\bUSD\b/g, '').trim();
  }

  function getSelectedItems(root) {
    return Array.from(root.querySelectorAll(`${itemSelector} .Cross_referral__input:checked`)).filter((input) => {
      const item = input.closest(itemSelector);
      return item && item.dataset.available === 'true';
    }).map((input) => input.closest(itemSelector));
  }

  function updateTotals(root) {
    if (!root) return;

    const items = getSelectedItems(root);
    const priceTotal = items.reduce((sum, item) => sum + cents(item.dataset.priceCents), 0);
    const compareTotal = items.reduce((sum, item) => {
      const price = cents(item.dataset.priceCents);
      const compare = cents(item.dataset.compareCents);
      return sum + Math.max(compare, price);
    }, 0);
    const hasCompare = compareTotal > priceTotal && items.some((item) => item.dataset.hasCompare === 'true');

    const totalPrice = root.querySelector('[data-cross-referral-total-price]');
    const totalCompare = root.querySelector('[data-cross-referral-total-compare]');
    if (totalPrice) totalPrice.textContent = formatMoney(root, priceTotal);
    if (totalCompare) {
      totalCompare.textContent = hasCompare ? formatMoney(root, compareTotal) : '';
      totalCompare.hidden = !hasCompare;
    }

    root.querySelectorAll(itemSelector).forEach((item) => {
      const input = item.querySelector('.Cross_referral__input');
      item.classList.toggle('is-selected', Boolean(input?.checked));
    });

    const addButton = root.querySelector('[data-cross-referral-add]');
    if (addButton) addButton.disabled = items.length === 0;
  }

  function applyVariant(item, option) {
    if (!item || !option) return;

    item.dataset.selectedVariantId = option.value;
    item.dataset.priceCents = option.dataset.priceCents || '0';
    item.dataset.compareCents = option.dataset.compareCents || item.dataset.priceCents;
    item.dataset.hasCompare = option.dataset.hasCompare || 'false';
    item.dataset.priceText = option.dataset.priceText || '';
    item.dataset.compareText = option.dataset.compareText || '';
    item.dataset.available = option.dataset.available || 'false';

    const input = item.querySelector('.Cross_referral__input');
    if (input) {
      input.value = option.value;
      input.disabled = item.dataset.available !== 'true';
      if (input.disabled) input.checked = false;
    }

    const price = item.querySelector('[data-cross-referral-item-price]');
    const compare = item.querySelector('[data-cross-referral-item-compare]');
    if (price) price.textContent = item.dataset.priceText;
    if (compare) {
      compare.textContent = item.dataset.compareText;
      compare.hidden = item.dataset.compareText === '';
    }

    const image = item.querySelector('.Cross_referral__image img');
    if (image && option.dataset.imageUrl) {
      image.src = option.dataset.imageUrl;
      image.alt = option.dataset.imageAlt || image.alt;
    }
  }

  function setDropdownOpen(dropdown, open) {
    if (!dropdown) return;

    dropdown.classList.toggle('is-open', open);
    dropdown.querySelectorAll('[data-cross-referral-dropdown-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    const panel = dropdown.querySelector('[data-cross-referral-dropdown-panel]');
    if (panel) panel.hidden = !open;
  }

  function closeDropdowns(scope = document, except = null) {
    scope.querySelectorAll(dropdownSelector).forEach((dropdown) => {
      if (dropdown !== except) setDropdownOpen(dropdown, false);
    });
  }

  function syncDropdown(select, option, showSelectedLabel = false) {
    const dropdown = select?.closest(dropdownSelector);
    if (!dropdown || !option) return;

    dropdown.querySelectorAll('[data-cross-referral-option]').forEach((button) => {
      button.setAttribute('aria-selected', button.dataset.value === option.value ? 'true' : 'false');
    });

    if (showSelectedLabel) {
      dropdown.querySelectorAll('[data-cross-referral-dropdown-label]').forEach((label) => {
        label.textContent = option.textContent.trim();
      });
    }
  }

  function openQuickAdd(item) {
    const trigger = item?.querySelector('.Cross_referral__quick-add modal-opener button');
    if (trigger && !trigger.disabled) trigger.click();
  }

  async function addSelected(root, addButton) {
    const selectedItems = getSelectedItems(root);
    if (!selectedItems.length || addButton?.disabled) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = {
      items: selectedItems.map((item) => ({
        id: Number(item.dataset.selectedVariantId),
        quantity: 1,
      })),
    };

    if (cart?.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement?.(addButton);
    }

    addButton.setAttribute('aria-disabled', 'true');
    addButton.classList.add('loading');

    try {
      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      config.body = JSON.stringify(body);

      const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
      if (response.status) throw new Error(response.description || response.message || 'Cart add failed');

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cross-referral',
          productVariantId: body.items.map((item) => item.id).join(','),
          cartData: response,
        });
      }

      if (cart?.renderContents) {
        cart.renderContents(response);
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (error) {
      console.error(error);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cross-referral',
          productVariantId: body.items.map((item) => item.id).join(','),
          errors: error.message,
          message: error.message,
        });
      }
    } finally {
      addButton.classList.remove('loading');
      addButton.removeAttribute('aria-disabled');
      updateTotals(root);
    }
  }

  function initCrossReferral(root) {
    if (!root || root.dataset.crossReferralReady === 'true') return;

    root.dataset.crossReferralReady = 'true';
    root.querySelectorAll('[data-cross-referral-variant]').forEach((select) => {
      applyVariant(select.closest(itemSelector), select.selectedOptions[0]);
      syncDropdown(select, select.selectedOptions[0], false);
    });
    updateTotals(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(rootSelector).forEach(initCrossReferral);
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cross-referral-variant]');
    if (select) {
      const root = select.closest(rootSelector);
      applyVariant(select.closest(itemSelector), select.selectedOptions[0]);
      syncDropdown(select, select.selectedOptions[0], select.dataset.crossReferralShowSelected === 'true');
      updateTotals(root);
      return;
    }

    const input = event.target.closest('.Cross_referral__input');
    if (input) updateTotals(input.closest(rootSelector));
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest(dropdownSelector)) closeDropdowns();

    const root = event.target.closest(rootSelector);
    if (!root) return;

    initCrossReferral(root);

    const optionButton = event.target.closest('[data-cross-referral-option]');
    if (optionButton && root.contains(optionButton)) {
      const dropdown = optionButton.closest(dropdownSelector);
      const select = dropdown?.querySelector('[data-cross-referral-variant]');
      if (select && optionButton.dataset.value) {
        event.preventDefault();
        select.value = optionButton.dataset.value;
        select.dataset.crossReferralShowSelected = 'true';
        select.dispatchEvent(new Event('change', { bubbles: true }));
        setDropdownOpen(dropdown, false);
      }
      return;
    }

    const dropdownTrigger = event.target.closest('[data-cross-referral-dropdown-trigger]');
    if (dropdownTrigger && root.contains(dropdownTrigger)) {
      const dropdown = dropdownTrigger.closest(dropdownSelector);
      const shouldOpen = !dropdown.classList.contains('is-open');

      event.preventDefault();
      closeDropdowns(root, dropdown);
      setDropdownOpen(dropdown, shouldOpen);
      return;
    }

    const modalButton = event.target.closest('[data-cross-referral-modal]');
    if (modalButton && root.contains(modalButton)) {
      event.preventDefault();
      openQuickAdd(modalButton.closest(itemSelector));
      return;
    }

    const addButton = event.target.closest('[data-cross-referral-add]');
    if (addButton && root.contains(addButton)) {
      event.preventDefault();
      addSelected(root, addButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDropdowns();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
